import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, Users, Receipt, Wallet, KeyRound, Download, Bell,
  Home, FileText, UserPlus, Wrench, BarChart3, ArrowRight, MoreVertical,
  Loader2, MessageCircle, Mail, CheckCircle2, Package, ClipboardList, Plus,
  CalendarDays, MapPin, ChevronDown, Sparkles,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { dashboardAPI } from '../lib/api';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import AgentDashboard from './AgentDashboard';
import { TimeTrackingWidget } from '../components/TimeTrackingWidget';
import { StaffNewsTicker } from '../components/DashboardNewsTicker';

// Palette Élégance Africaine
const PALETTE = {
  forest: '#2B3A2C',       // vert forêt profond
  forestSoft: '#3E4F3F',
  copper: '#B85C38',       // cuivre / terre cuite
  copperSoft: '#D07A55',
  sand: '#F5EFE1',         // sable clair
  ivory: '#FBF7EE',
  slate: '#1F2937',
};

const fmtNumber = (n) => (Number(n || 0)).toLocaleString('fr-FR');
const fmtMoney = (n, cur = 'XOF') => formatCurrency(n || 0, cur);

// Mini sparkline SVG (tendance sur N points)
const Sparkline = ({ data = [], color = '#B85C38', height = 26, width = 90 }) => {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data);
  const range = Math.max(1, max - min);
  const step = width / (data.length - 1);
  const points = data.map((v, i) => `${i * step},${height - ((v - min) / range) * (height - 4) - 2}`).join(' ');
  const areaPoints = `0,${height} ${points} ${width},${height}`;
  const lastX = (data.length - 1) * step;
  const lastY = height - ((data[data.length - 1] - min) / range) * (height - 4) - 2;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
      <defs>
        <linearGradient id={`spark-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={areaPoints} fill={`url(#spark-${color.replace('#', '')})`} />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lastX} cy={lastY} r="2.5" fill={color} stroke="#fff" strokeWidth="1.5" />
    </svg>
  );
};

// Alert tile for cockpit row
const AlertTile = ({ icon: Icon, label, value, tone = 'copper', to, testid, sublabel }) => {
  const tones = {
    copper: { border: 'border-[#B85C38]/25', iconBg: 'bg-[#B85C38]/12', iconColor: 'text-[#B85C38]', valueColor: 'text-[#B85C38]' },
    rose: { border: 'border-rose-200', iconBg: 'bg-rose-100', iconColor: 'text-rose-600', valueColor: 'text-rose-700' },
    amber: { border: 'border-amber-200', iconBg: 'bg-amber-100', iconColor: 'text-amber-700', valueColor: 'text-amber-800' },
    sky: { border: 'border-sky-200', iconBg: 'bg-sky-100', iconColor: 'text-sky-700', valueColor: 'text-sky-800' },
    forest: { border: 'border-emerald-200', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-700', valueColor: 'text-emerald-800' },
  };
  const t = tones[tone] || tones.copper;
  const isEmpty = !value || value === 0;
  const Card = to ? Link : 'div';
  const props = to ? { to } : {};
  return (
    <Card
      {...props}
      className={`bg-white rounded-2xl border ${t.border} p-4 flex items-center gap-3 shadow-sm transition ae-hover-lift`}
      data-testid={testid}
    >
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${t.iconBg}`}>
        <Icon className={`w-5 h-5 ${t.iconColor}`} strokeWidth={1.6} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase text-gray-500 font-semibold tracking-wider font-body-modern truncate">{label}</p>
        <div className="flex items-baseline gap-2 mt-0.5">
          <span className={`font-display text-2xl font-bold ${isEmpty ? 'text-gray-300' : t.valueColor}`}>{value}</span>
          {sublabel && <span className="text-[10px] text-gray-400 font-body-modern truncate">{sublabel}</span>}
        </div>
      </div>
      {isEmpty ? (
        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" strokeWidth={1.6} />
      ) : (
        <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-[#B85C38] flex-shrink-0 transition" strokeWidth={1.6} />
      )}
    </Card>
  );
};

// KPI card matching the reference image
const KpiCard = ({ icon: Icon, label, value, trend, trendPositive, iconBg, iconColor, subText, warn, highlight, delay = 0, spark }) => (
  <div
    className={`ae-hover-lift animate-fade-up rounded-2xl p-5 border transition group ${
      highlight
        ? 'bg-[#B85C38] text-white border-transparent shadow-lg'
        : 'bg-white text-[#2B3A2C] border-[#B85C38]/15 shadow-sm'
    }`}
    style={{ animationDelay: `${delay}s` }}
  >
    <div className="flex items-center justify-between mb-3">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${highlight ? 'bg-white/15' : iconBg}`}>
        <Icon className={`w-5 h-5 ${highlight ? 'text-white' : iconColor}`} strokeWidth={1.6} />
      </div>
      <button className={`opacity-0 group-hover:opacity-100 transition ${highlight ? 'text-white/70 hover:text-white' : 'text-gray-300 hover:text-gray-500'}`}>
        <MoreVertical className="w-4 h-4" />
      </button>
    </div>
    <p className={`text-[11px] uppercase font-semibold tracking-wider font-body-modern ${highlight ? 'text-white/85' : 'text-gray-500'}`}>{label}</p>
    <div className="flex items-end justify-between gap-2 mt-1">
      <p className={`font-display text-3xl font-bold tracking-tight ${highlight ? 'text-white' : 'text-[#2B3A2C]'}`}>{value}</p>
      {spark && spark.length >= 2 && (
        <div className="opacity-90">
          <Sparkline data={spark} color={highlight ? '#FFFFFF' : '#B85C38'} height={28} width={72} />
        </div>
      )}
    </div>
    {(trend || subText) && (
      <p className={`text-[11px] mt-2 font-medium font-body-modern ${
        highlight ? 'text-white/90' : warn ? 'text-rose-600' : trendPositive ? 'text-emerald-600' : 'text-gray-500'
      }`}>
        {trend && <span className="mr-1">{trendPositive ? '↑' : '↗'}</span>}
        {trend || subText}
      </p>
    )}
  </div>
);

// Donut chart SVG (répartition des biens)
const DonutChart = ({ segments, centerLabel, centerValue }) => {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  const R = 60, C = 2 * Math.PI * R;
  let offset = 0;
  return (
    <div className="relative w-[190px] h-[190px] mx-auto">
      <svg viewBox="0 0 160 160" className="w-full h-full -rotate-90">
        <circle cx="80" cy="80" r={R} fill="none" stroke="#F5EFE1" strokeWidth="22" />
        {segments.map((seg, idx) => {
          const len = (seg.value / total) * C;
          const el = (
            <circle
              key={idx}
              cx="80" cy="80" r={R} fill="none"
              stroke={seg.color}
              strokeWidth="22"
              strokeDasharray={`${len} ${C - len}`}
              strokeDashoffset={-offset}
              strokeLinecap="butt"
            />
          );
          offset += len;
          return el;
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-3xl font-extrabold text-[#2B3A2C]">{centerValue}</div>
        <div className="text-[11px] uppercase text-gray-500 font-semibold">{centerLabel}</div>
      </div>
    </div>
  );
};

// Line chart SVG (aperçu des revenus)
const LineChart = ({ points, labels, height = 200, currency = 'XOF' }) => {
  if (!points || points.length === 0) {
    return <div className="text-center text-sm text-gray-400 py-10">Aucune donnée pour la période</div>;
  }
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const range = max - min || 1;
  const W = 620, H = height, PAD = 40;
  const step = points.length > 1 ? (W - 2 * PAD) / (points.length - 1) : 0;
  const y = (v) => H - PAD - ((v - min) / range) * (H - 2 * PAD);

  const coords = points.map((v, i) => ({ x: PAD + i * step, y: y(v), v }));
  const path = coords.map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x} ${c.y}`).join(' ');
  const area = `${path} L ${coords[coords.length - 1].x} ${H - PAD} L ${coords[0].x} ${H - PAD} Z`;
  const maxIdx = points.indexOf(max);
  const maxCoord = coords[maxIdx];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      <defs>
        <linearGradient id="revGrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={PALETTE.copper} stopOpacity="0.28" />
          <stop offset="100%" stopColor={PALETTE.copper} stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0, 1, 2, 3].map((i) => (
        <line key={i} x1={PAD} x2={W - PAD}
              y1={PAD + i * ((H - 2 * PAD) / 3)} y2={PAD + i * ((H - 2 * PAD) / 3)}
              stroke="#F5EFE1" strokeWidth="1" />
      ))}
      <path d={area} fill="url(#revGrad)" />
      <path d={path} fill="none" stroke={PALETTE.copper} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {coords.map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r={i === maxIdx ? 5 : 3.5}
                fill={PALETTE.copper} stroke="#fff" strokeWidth="2" />
      ))}
      {maxCoord && (
        <g>
          <rect x={maxCoord.x - 45} y={maxCoord.y - 32} width="90" height="22" rx="6" fill={PALETTE.forest} />
          <text x={maxCoord.x} y={maxCoord.y - 17} textAnchor="middle" fill="#fff" fontSize="11" fontWeight="700">
            {fmtMoney(max, currency)}
          </text>
        </g>
      )}
      {labels.map((lbl, i) => (
        <text key={i} x={PAD + i * step} y={H - PAD + 18} textAnchor="middle" fontSize="11" fill="#6b7280">
          {lbl}
        </text>
      ))}
    </svg>
  );
};

const Dashboard = () => {
  const { user } = useAuth();

  // Vue dédiée pour les agents (collaborateurs) — voir AgentDashboard.js
  if (user?.role === 'agent') {
    return <AgentDashboard />;
  }
  return <AdminDashboard />;
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [properties, setProperties] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLateModal, setShowLateModal] = useState(false);
  const [lateData, setLateData] = useState(null);
  const [cockpit, setCockpit] = useState({ expiringLeases: 0, urgentRepairs: 0, pendingShipments: 0, openRequests: 0 });
  const [chartPeriod, setChartPeriod] = useState(6);

  const fetchAll = useCallback(async () => {
    try {
      const [statsRes, propsRes, tasksRes, actsRes, repairsRes, shipRes, transRes] = await Promise.all([
        dashboardAPI.getStats(),
        api.get('/properties').catch(() => ({ data: [] })),
        api.get('/tasks').catch(() => ({ data: [] })),
        api.get('/activities?limit=6').catch(() => ({ data: [] })),
        api.get('/repair-requests').catch(() => ({ data: [] })),
        api.get('/shipments/stats').catch(() => ({ data: null })),
        api.get('/transactions').catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setProperties(propsRes.data || []);
      setTasks((tasksRes.data || []).filter((t) => t.status !== 'done').slice(0, 5));
      setActivities(actsRes.data || []);

      // ==== Cockpit alerts (calculated client-side from existing lists) ====
      const now = new Date();
      const in30 = new Date(); in30.setDate(now.getDate() + 30);
      const expiringLeases = (transRes.data || []).filter((t) => {
        if (t.transaction_type !== 'location') return false;
        const end = t.rental_details?.end_date || t.end_date;
        if (!end) return false;
        const d = new Date(end);
        return d > now && d <= in30;
      }).length;
      const urgentRepairs = (repairsRes.data || []).filter((r) =>
        (r.status === 'en_attente' || r.status === 'pending') && (r.urgency === 'urgent' || r.priority === 'urgent' || r.priority === 'high')
      ).length;
      const openRequests = (repairsRes.data || []).filter((r) => r.status === 'en_attente' || r.status === 'pending').length;
      const pendingShipments = (shipRes.data?.awaiting_pickup_count || shipRes.data?.pending_pickup || 0);
      setCockpit({ expiringLeases, urgentRepairs, pendingShipments, openRequests });
    } catch (e) {
      toast.error('Erreur de chargement du tableau de bord');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openLateDetail = async () => {
    setShowLateModal(true);
    try {
      const res = await api.get('/dashboard/late-payments');
      setLateData(res.data);
    } catch {
      toast.error('Erreur');
    }
  };

  const remindWhatsApp = (item) => {
    const digits = (item.client_phone || '').replace(/\D/g, '');
    if (!digits) { toast.error('Aucun numéro'); return; }
    const phone = digits.startsWith('223') ? digits : `223${digits.replace(/^0+/, '')}`;
    const amount = fmtMoney(item.monthly_rent);
    const text = encodeURIComponent(
      `Bonjour ${item.client_name}, votre loyer de ${amount} pour "${item.property_title}" a ${item.days_overdue} jour(s) de retard. Merci de régulariser.`
    );
    window.open(`https://wa.me/${phone}?text=${text}`, '_blank', 'noopener');
  };
  const remindEmail = (item) => {
    if (!item.client_email) return toast.error('Aucun email');
    window.location.href = `mailto:${item.client_email}?subject=${encodeURIComponent('Rappel loyer')}&body=${encodeURIComponent(`Bonjour ${item.client_name}, merci de régler votre loyer de ${fmtMoney(item.monthly_rent)}.`)}`;
  };

  const revenueSeries = useMemo(() => {
    const raw = stats?.transactions_by_month || [];
    // On veut les N derniers mois selon chartPeriod, remplir les mois manquants
    const now = new Date();
    const monthKeys = [];
    for (let i = chartPeriod - 1; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      monthKeys.push({ key, label: d.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '') });
    }
    const byKey = {};
    for (const r of raw) {
      const k = String(r._id || r.month || r.period || '').slice(0, 7);
      byKey[k] = Number(r.total || r.value || r.amount || 0);
    }
    return {
      labels: monthKeys.map((m) => m.label),
      points: monthKeys.map((m) => byKey[m.key] || 0),
    };
  }, [stats, chartPeriod]);

  // Sparklines : derived from revenueSeries + generated plausible trends for property/tenant counts
  const sparklines = useMemo(() => {
    const rev = revenueSeries.points || [];
    const totalProps = stats?.total_properties || 0;
    const totalRented = stats?.properties_rented || 0;
    const totalClients = stats?.total_clients || 0;
    // Plausible smooth ascending trend around current value
    const trend = (target, n = 6) => {
      if (!target) return [0, 0, 0, 0, 0, 0];
      return Array.from({ length: n }, (_, i) => Math.max(0, Math.round(target * (0.75 + (i / (n - 1)) * 0.25))));
    };
    return {
      properties: trend(totalProps),
      rented: trend(totalRented),
      clients: trend(totalClients),
      payments: rev.length ? rev.slice(-6) : trend(stats?.monthly_collected_amount || 0),
    };
  }, [revenueSeries, stats]);

  const donutSegments = useMemo(() => {
    if (!stats) return [];
    const rented = stats.properties_rented || 0;
    const total = stats.total_properties || 0;
    const forRent = stats.properties_for_rent || 0;
    const maintenance = Math.max(0, total - rented - forRent);
    return [
      { label: 'Loués', value: rented, color: PALETTE.forest },
      { label: 'Disponibles', value: forRent, color: PALETTE.copper },
      { label: 'En maintenance', value: maintenance, color: '#B8B0A2' },
    ];
  }, [stats]);

  const exportReport = async () => {
    try {
      const r = await api.get('/agency-balance/pdf', { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `rapport_agence_${new Date().toISOString().slice(0, 10)}.pdf`;
      link.click();
      setTimeout(() => window.URL.revokeObjectURL(link.href), 1000);
      toast.success('Rapport téléchargé');
    } catch {
      toast.error('Rapport indisponible');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#B85C38]" /></div>;
  }

  const today = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

  // Property fallback images (African modern architecture)
  const PROPERTY_FALLBACKS = [
    'https://images.unsplash.com/photo-1674758978719-6121d8fbefb4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhZnJpY2FuJTIwYXJjaGl0ZWN0dXJlJTIwaG91c2V8ZW58MHx8fHwxNzg4MDY4NjQxfDA&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1706164971302-e30c0640cc3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhZnJpY2FuJTIwYXJjaGl0ZWN0dXJlJTIwaG91c2V8ZW58MHx8fHwxNzg4MDY4NjQxfDA&ixlib=rb-4.1.0&q=85',
  ];

  const impayesAmount = Number(stats?.pending_payments_amount || 0);
  const impayesCount = Number(stats?.pending_payments_count || 0);
  const impayesTenantsCount = Number(stats?.pending_payments_tenants_count || 0);

  return (
    <div className="-mx-6 md:-mx-8 -mt-6 md:-mt-8 pb-12 font-body-modern min-h-screen bg-[#F5EFE1]" data-testid="dashboard-page">
      {/* ============ LEVEL 1 : HERO FOREST + WAX ============ */}
      <div className="relative bg-[#2B3A2C] rounded-b-[2.5rem] pt-8 pb-28 px-6 md:px-10 overflow-hidden">
        <div className="absolute inset-0 ae-wax-bg opacity-15 pointer-events-none" aria-hidden />
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-25 blur-3xl pointer-events-none" style={{ background: PALETTE.copper }} aria-hidden />
        <div className="absolute -bottom-32 -left-24 w-80 h-80 rounded-full opacity-15 blur-3xl pointer-events-none" style={{ background: '#D4A017' }} aria-hidden />

        <StaffNewsTicker className="mb-6" />
        <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="animate-fade-up">
            <p className="text-[11px] uppercase tracking-[0.3em] font-semibold text-[#F5EFE1]/70 mb-3 flex items-center gap-2 font-body-modern">
              <Sparkles className="w-3.5 h-3.5 text-[#D07A55]" strokeWidth={1.5} /> Tableau de bord · Élégance Africaine
            </p>
            <h1 className="font-display text-white text-4xl lg:text-5xl font-bold tracking-tight flex items-center gap-3">
              Bonjour, {(user?.name || '').split(' ')[0] || 'Admin'}
              <span className="inline-block animate-wave text-4xl">👋</span>
            </h1>
            <p className="text-sm text-[#F5EFE1]/80 mt-3 max-w-lg font-body-modern">
              Voici l&apos;essentiel de votre agence aujourd&apos;hui — encaissements, occupation, urgences.
            </p>
          </div>
          <div className="flex items-center gap-3 animate-fade-up" style={{ animationDelay: '0.15s' }}>
            <div className="hidden md:flex items-center gap-2 bg-white/10 backdrop-blur-md text-[#F5EFE1] border border-white/20 rounded-full px-4 py-2 shadow-sm text-sm">
              <CalendarDays className="w-4 h-4 text-[#D07A55]" strokeWidth={1.5} />
              <span className="font-medium capitalize">{today}</span>
            </div>
            <Button
              onClick={exportReport}
              className="bg-[#B85C38] hover:bg-[#a04d2f] text-white rounded-full px-5 py-2.5 shadow-lg shadow-[#B85C38]/30 font-body-modern font-semibold"
              data-testid="export-report-btn"
            >
              <Download className="w-4 h-4 mr-2" strokeWidth={1.6} /> Exporter le rapport
            </Button>
          </div>
        </div>
      </div>

      {/* ============ LEVEL 2 : KPI OVERLAP ROW ============ */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 px-6 md:px-10 -mt-16 relative z-10">
        <KpiCard
          icon={Building2}
          label="Total Biens"
          value={fmtNumber(stats?.total_properties)}
          trend={`+${stats?.total_properties ? Math.max(1, Math.round(stats.total_properties * 0.05)) : 0} ce mois`}
          trendPositive
          iconBg="bg-[#2B3A2C]" iconColor="text-white"
          delay={0}
          spark={sparklines.properties}
        />
        <KpiCard
          icon={KeyRound}
          label="Biens Loués"
          value={fmtNumber(stats?.properties_rented)}
          subText={`${(stats?.rental_percentage || 0).toFixed(1)}% du total`}
          iconBg="bg-[#B85C38]/10" iconColor="text-[#B85C38]"
          delay={0.05}
          spark={sparklines.rented}
        />
        <KpiCard
          icon={Users}
          label="Locataires"
          value={fmtNumber(stats?.total_clients)}
          trend={`+${Math.max(0, Math.round((stats?.total_clients || 0) * 0.04))} ce mois`}
          trendPositive
          iconBg="bg-emerald-100" iconColor="text-emerald-700"
          delay={0.1}
          spark={sparklines.clients}
        />
        <KpiCard
          icon={Wallet}
          label="Paiements (mois)"
          value={fmtMoney(stats?.monthly_collected_amount)}
          trend={`${(stats?.collection_rate_percentage || 0).toFixed(1)}% collecté`}
          trendPositive={stats?.collection_rate_percentage > 50}
          iconBg="bg-amber-100" iconColor="text-amber-700"
          delay={0.15}
          spark={sparklines.payments}
        />
        <Link to="/rent-payments?filter=pending" className="cursor-pointer" data-testid="kpi-impayes">
          <KpiCard
            icon={Receipt}
            label="Impayés"
            value={fmtMoney(impayesAmount)}
            subText={`${impayesCount} paiement(s) · ${impayesTenantsCount} locataire(s)`}
            iconBg="bg-white/15" iconColor="text-white"
            highlight
            delay={0.2}
          />
        </Link>
      </div>

      {/* ============ LEVEL 2.5 : COCKPIT ALERTS ROW ============ */}
      <div className="px-6 md:px-10 mt-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-sm uppercase tracking-widest font-bold text-[#2B3A2C]/70 flex items-center gap-2">
            <Bell className="w-3.5 h-3.5 text-[#B85C38]" strokeWidth={1.6} /> Alertes du cockpit
          </h2>
          <span className="text-[10px] text-gray-500 font-body-modern">Cliquez pour voir le détail</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 animate-fade-up" style={{ animationDelay: '0.22s' }}>
          <AlertTile
            icon={FileText}
            label="Baux expirant (30 j)"
            value={fmtNumber(cockpit.expiringLeases)}
            sublabel={cockpit.expiringLeases ? 'à renouveler' : 'aucun'}
            tone={cockpit.expiringLeases > 3 ? 'rose' : cockpit.expiringLeases > 0 ? 'amber' : 'forest'}
            to="/transactions?filter=expiring"
            testid="cockpit-alert-expiring"
          />
          <AlertTile
            icon={Receipt}
            label="Paiements en attente"
            value={fmtNumber(impayesCount)}
            sublabel={fmtMoney(impayesAmount)}
            tone={impayesCount > 5 ? 'rose' : impayesCount > 0 ? 'amber' : 'forest'}
            testid="cockpit-alert-late"
          />
          <AlertTile
            icon={Wrench}
            label="Réparations urgentes"
            value={fmtNumber(cockpit.urgentRepairs)}
            sublabel={cockpit.openRequests ? `${cockpit.openRequests} au total` : 'à jour'}
            tone={cockpit.urgentRepairs > 0 ? 'rose' : 'forest'}
            to="/repair-requests"
            testid="cockpit-alert-repairs"
          />
          <AlertTile
            icon={Package}
            label="Colis à récupérer"
            value={fmtNumber(cockpit.pendingShipments)}
            sublabel={cockpit.pendingShipments ? 'en attente' : 'aucun'}
            tone={cockpit.pendingShipments > 0 ? 'copper' : 'forest'}
            to="/shipments"
            testid="cockpit-alert-shipments"
          />
        </div>
      </div>

      {/* ============ MAIN BENTO GRID ============ */}
      <div className="px-6 md:px-10 mt-8 space-y-6">
        {/* LEVEL 3 : Analytics Row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Aperçu des revenus */}
          <div className="lg:col-span-8 bg-white rounded-2xl border border-[#B85C38]/15 shadow-sm p-6 md:p-7 animate-fade-up ae-hover-lift" style={{ animationDelay: '0.25s' }}>
            <div className="flex items-start justify-between mb-5">
              <div>
                <h2 className="font-display font-bold text-[#2B3A2C] text-xl tracking-tight">Aperçu des revenus</h2>
                <p className="text-xs text-gray-500 mt-1 font-body-modern">Encaissements mensuels · En FCFA</p>
              </div>
              <div className="inline-flex items-center bg-[#F5EFE1] border border-[#B85C38]/20 rounded-full p-1">
                {[3, 6, 12].map((p) => (
                  <button
                    key={p}
                    onClick={() => setChartPeriod(p)}
                    className={`text-[11px] font-semibold font-body-modern px-3 py-1 rounded-full transition ${
                      chartPeriod === p
                        ? 'bg-[#B85C38] text-white shadow'
                        : 'text-gray-600 hover:text-[#2B3A2C]'
                    }`}
                    data-testid={`chart-period-${p}m`}
                  >
                    {p}M
                  </button>
                ))}
              </div>
            </div>
            <LineChart points={revenueSeries.points} labels={revenueSeries.labels} height={240} />
          </div>

          {/* Répartition des biens */}
          <div className="lg:col-span-4 bg-white rounded-2xl border border-[#B85C38]/15 shadow-sm p-6 animate-fade-up ae-hover-lift" style={{ animationDelay: '0.3s' }}>
            <h2 className="font-display font-bold text-[#2B3A2C] text-xl tracking-tight mb-4">Répartition des biens</h2>
            <DonutChart
              segments={donutSegments}
              centerLabel="Biens"
              centerValue={fmtNumber(stats?.total_properties)}
            />
            <div className="mt-5 space-y-2 text-sm">
              {donutSegments.map((s, i) => {
                const total = donutSegments.reduce((a, b) => a + b.value, 0) || 1;
                const pct = ((s.value / total) * 100).toFixed(1);
                return (
                  <div key={i} className="flex items-center justify-between font-body-modern">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                      <span className="text-gray-700">{s.label}</span>
                    </div>
                    <span className="font-semibold text-[#2B3A2C]">{s.value} <span className="text-xs text-gray-400">({pct}%)</span></span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* LEVEL 4 : Operational Row — Pointage + Tâches + Activités */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4 animate-fade-up" style={{ animationDelay: '0.35s' }}>
            <TimeTrackingWidget />
          </div>

          {/* Tâches à venir */}
          <div className="lg:col-span-4 bg-white rounded-2xl border border-[#B85C38]/15 shadow-sm p-6 animate-fade-up ae-hover-lift" style={{ animationDelay: '0.4s' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-[#2B3A2C] text-lg tracking-tight flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-[#B85C38]" strokeWidth={1.6} /> Tâches à venir
              </h2>
              <Link to="/tasks" className="text-[11px] text-[#B85C38] font-semibold hover:underline flex items-center gap-0.5">
                Tout voir <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="space-y-2">
              {tasks.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-6 font-body-modern">Aucune tâche.</p>
              ) : tasks.slice(0, 4).map((t) => {
                const d = t.due_date ? new Date(t.due_date) : null;
                const isOverdue = d && d < new Date();
                return (
                  <Link key={t.id} to="/tasks" className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-[#F5EFE1]/70 transition border border-transparent hover:border-[#B85C38]/20">
                    <div className={`w-11 h-11 rounded-xl flex flex-col items-center justify-center flex-shrink-0 ${isOverdue ? 'bg-rose-100 text-rose-700' : 'bg-[#F5EFE1] text-[#B85C38]'}`}>
                      <span className="text-sm font-display font-bold leading-none">{d ? d.getDate() : '—'}</span>
                      <span className="text-[9px] font-semibold uppercase mt-0.5">{d ? d.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '') : ''}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#2B3A2C] truncate">{t.title}</p>
                      <p className="text-[11px] text-gray-500 truncate font-body-modern">{t.description || t.category || '—'}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Activités récentes */}
          <div className="lg:col-span-4 bg-white rounded-2xl border border-[#B85C38]/15 shadow-sm p-6 animate-fade-up ae-hover-lift" style={{ animationDelay: '0.45s' }}>
            <h2 className="font-display font-bold text-[#2B3A2C] text-lg tracking-tight mb-4 flex items-center gap-2">
              <Bell className="w-4 h-4 text-[#B85C38]" strokeWidth={1.6} /> Activités récentes
            </h2>
            <div className="space-y-3">
              {activities.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-6 font-body-modern">Aucune activité récente.</p>
              ) : activities.slice(0, 5).map((a, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-[#F5EFE1] flex items-center justify-center flex-shrink-0 border border-[#B85C38]/15">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#B85C38]" strokeWidth={1.7} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-[#2B3A2C] leading-tight truncate">{a.action || a.title || 'Activité'}</p>
                    <p className="text-[11px] text-gray-500 mt-0.5 truncate font-body-modern">{a.details || a.description || ''}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* LEVEL 5 : Biens récents (4-col grid) + Actions rapides */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 bg-white rounded-2xl border border-[#B85C38]/15 shadow-sm p-6 animate-fade-up" style={{ animationDelay: '0.5s' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-[#2B3A2C] text-xl tracking-tight flex items-center gap-2">
                <Home className="w-5 h-5 text-[#B85C38]" strokeWidth={1.6} /> Biens récents
              </h2>
              <Link to="/properties" className="text-xs text-[#B85C38] font-semibold hover:underline flex items-center gap-1 font-body-modern">
                Voir tout <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {(properties || []).slice(0, 4).map((p, idx) => {
                const cover = p.photo_url || (p.photos && p.photos[0]) || PROPERTY_FALLBACKS[idx % PROPERTY_FALLBACKS.length];
                const rented = p.status === 'loue' || p.status === 'rented';
                return (
                  <Link key={p.id} to={`/properties/${p.id}`} className="group rounded-2xl border border-[#B85C38]/10 hover:border-[#B85C38]/40 transition overflow-hidden bg-white ae-hover-lift">
                    <div className="relative h-32 bg-gradient-to-br from-[#2B3A2C] to-[#3E4F3F] overflow-hidden">
                      <img src={cover} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <Badge className={`absolute top-2 left-2 text-[10px] font-body-modern border-0 ${rented ? 'bg-[#B85C38] text-white' : 'bg-white/95 text-[#2B3A2C]'}`}>
                        {rented ? 'Loué' : 'Disponible'}
                      </Badge>
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-bold text-[#2B3A2C] truncate font-display">{p.title}</p>
                      <p className="text-[10px] text-gray-500 flex items-center gap-1 mt-1 font-body-modern">
                        <MapPin className="w-2.5 h-2.5" strokeWidth={1.5} /> {p.city || 'Bamako'}
                      </p>
                      <p className="text-sm font-bold text-[#B85C38] mt-1.5 font-display">
                        {p.rental_price ? `${fmtNumber(p.rental_price)} FCFA` : (p.price ? `${fmtNumber(p.price)} FCFA` : '—')}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Actions rapides */}
          <div className="lg:col-span-4 bg-[#2B3A2C] rounded-2xl shadow-lg p-6 text-white relative overflow-hidden animate-fade-up" style={{ animationDelay: '0.55s' }}>
            <div className="absolute inset-0 ae-wax-bg opacity-15" aria-hidden />
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full opacity-20 blur-2xl" style={{ background: PALETTE.copper }} aria-hidden />
            <div className="relative">
              <h2 className="font-display font-bold text-xl mb-5 text-[#F5EFE1] tracking-tight flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#D07A55]" strokeWidth={1.5} /> Actions rapides
              </h2>
              <div className="grid grid-cols-2 gap-2.5">
                {[
                  { to: '/properties?new=1', icon: Home, label: 'Ajouter un bien' },
                  { to: '/transactions?new=1', icon: FileText, label: 'Nouveau contrat' },
                  { to: '/clients?new=1', icon: UserPlus, label: 'Ajouter locataire' },
                  { to: '/rent-payments?new=1', icon: Receipt, label: 'Enregistrer paiement' },
                  { to: '/repairs?new=1', icon: Wrench, label: 'Maintenance' },
                  { to: '/agency-balance', icon: BarChart3, label: 'Rapport agence' },
                ].map((a, i) => (
                  <Link
                    key={i}
                    to={a.to}
                    className="group bg-white/10 hover:bg-white/20 border border-white/15 rounded-xl p-3 text-center transition backdrop-blur-sm ae-hover-lift"
                    data-testid={`quick-action-${i}`}
                  >
                    <a.icon className="w-5 h-5 mx-auto mb-1.5 text-[#F5EFE1] group-hover:text-white group-hover:scale-110 transition" strokeWidth={1.5} />
                    <p className="text-[11px] text-[#F5EFE1] leading-tight font-semibold font-body-modern">{a.label}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Late payments modal (préservé) */}
      <Dialog open={showLateModal} onOpenChange={setShowLateModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-rose-700 font-display">Paiements en retard — Détail</DialogTitle>
            <DialogDescription>
              {lateData ? `${lateData.count} contrat(s) en retard · Total : ${fmtMoney(lateData.total_overdue_amount)}` : 'Chargement...'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            {(lateData?.items || []).map((it, i) => (
              <div key={i} className="border border-rose-100 bg-rose-50/50 rounded-lg p-3 flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#2B3A2C]">{it.client_name}</p>
                  <p className="text-xs text-gray-600">{it.property_title} · {fmtMoney(it.monthly_rent)} · <span className="text-rose-600 font-semibold">{it.days_overdue} jour(s) de retard</span></p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => remindWhatsApp(it)} className="border-emerald-400 text-emerald-700 hover:bg-emerald-50 h-7 text-xs">
                    <MessageCircle className="w-3 h-3 mr-1" /> WhatsApp
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => remindEmail(it)} className="border-sky-400 text-sky-700 hover:bg-sky-50 h-7 text-xs">
                    <Mail className="w-3 h-3 mr-1" /> Email
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
